import { NavLink, Link, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import {
  Inbox, Users, MessageSquare, Bell, BarChart3, LifeBuoy, MessagesSquare,
  FileText, Sparkles, MailPlus, Building2, ScrollText, Settings,
  LogOut, Download,
} from "lucide-react";
import { BRAND } from "@/config/brand";
import { adminApi } from "@/lib/api";

const TABS = [
  { to: "/admin/submissions", icon: Inbox, label: "Submissions" },
  { to: "/admin/users", icon: Users, label: "User Accounts" },
  { to: "/admin/live-chat", icon: MessageSquare, label: "Live Chat", badgeKey: "live_chat" },
  { to: "/admin/notifications", icon: Bell, label: "Notifications" },
  { to: "/admin/analytics", icon: BarChart3, label: "Analytics" },
  { to: "/admin/payouts", icon: LifeBuoy, label: "Payouts" },
  { to: "/admin/ai-transcripts", icon: MessagesSquare, label: "All Chats" },
  { to: "/admin/blog", icon: FileText, label: "Blog" },
  { to: "/admin/knowledge-base", icon: Sparkles, label: "Knowledge Base" },
  { to: "/admin/support-inbox", icon: MailPlus, label: "Support Inbox", badgeKey: "support_inbox" },
  { to: "/admin/vendors", icon: Building2, label: "Vendor Reports" },
  { to: "/admin/audit-log", icon: ScrollText, label: "Audit Log" },
  { to: "/admin/settings", icon: Settings, label: "Settings" },
];

const useBadges = () => {
  const [badges, setBadges] = useState({});
  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const [chats, emails] = await Promise.all([
          adminApi.get("/admin/chat-sessions").catch(() => ({ data: [] })),
          adminApi.get("/admin/support-emails").catch(() => ({ data: [] })),
        ]);
        if (cancelled) return;
        setBadges({
          live_chat: (chats.data || []).filter((c) => c.status !== "closed").length,
          support_inbox: (emails.data || []).length,
        });
      } catch { /* ignore */ }
    };
    load();
    const t = setInterval(load, 60000);
    return () => { cancelled = true; clearInterval(t); };
  }, []);
  return badges;
};

const installPWA = () => {
  if (window.__nexonInstallPrompt) {
    window.__nexonInstallPrompt.prompt();
  } else {
    alert("To install: use your browser's 'Add to Home Screen' or 'Install app' option in the address bar menu.");
  }
};

export default function AdminLayout({ children }) {
  const nav = useNavigate();
  const badges = useBadges();

  const logout = () => {
    localStorage.removeItem("nexon_admin_token");
    nav("/admin/login");
  };

  return (
    <div className="min-h-screen bg-[#FAF9FE] flex flex-col" style={{ fontFamily: "'DM Sans','Inter',system-ui,sans-serif" }}>
      {/* Top header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-[1600px] mx-auto px-6 py-4 flex items-center justify-between gap-4">
            <Link to="/admin/submissions" className="flex items-center gap-3" data-testid="admin-brand-link">
              <img src="/logos/nexon-logo.webp" alt="GC Nexon" className="h-14 w-auto object-contain" />
              <div className="text-xs text-[#7A7A99] tracking-wide border-l border-slate-200 pl-3">Admin console</div>
            </Link>
          <div className="flex items-center gap-2">
            <button onClick={installPWA} data-testid="admin-install-app" className="hidden sm:inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-[#C9E9A4] text-[#3E7A1E] border border-[#B4DE8A] hover:bg-[#B4DE8A] text-sm font-bold transition">
              <Download size={14} /> Install app
            </button>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 text-sm font-bold transition">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      {/* Horizontal tabs */}
      <nav className="bg-white border-b border-slate-200 sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6">
          <div className="flex items-center gap-1 overflow-x-auto no-scrollbar">
            {TABS.map((t, i) => {
              const badge = t.badgeKey ? badges[t.badgeKey] : 0;
              return (
                <NavLink
                  key={i + t.label}
                  to={t.to}
                  data-testid={`admin-nav-${t.label.toLowerCase().replace(/\s/g, '-')}`}
                  className={({ isActive }) =>
                    `relative flex items-center gap-2 px-4 py-3 text-sm font-bold whitespace-nowrap transition-colors ${
                      isActive ? "text-slate-900" : "text-slate-500 hover:text-slate-800"
                    } group`
                  }
                >
                  {({ isActive }) => (
                    <>
                      <t.icon size={15} />
                      <span>{t.label}</span>
                      {badge > 0 && (
                        <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-rose-500 text-white text-[10px] font-black">
                          {badge > 999 ? "999+" : badge}
                        </span>
                      )}
                      {isActive && (
                        <span className="absolute left-2 right-2 -bottom-px h-[3px] rounded-t-full bg-gradient-to-r from-indigo-500 via-indigo-600 to-emerald-400" />
                      )}
                    </>
                  )}
                </NavLink>
              );
            })}
          </div>
        </div>
      </nav>

      <main className="flex-1 max-w-[1600px] w-full mx-auto">
        {children}
      </main>

      <footer className="max-w-[1600px] mx-auto w-full px-6 py-4 text-xs text-slate-400">
        {BRAND.name} · Admin console · Secure area — do not share credentials.
      </footer>

      <style>{`.no-scrollbar::-webkit-scrollbar{display:none}.no-scrollbar{scrollbar-width:none}`}</style>
    </div>
  );
}
